import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useClientTranslation } from '@/lib/core/i18n';
import type { TranslationKey } from '@/lib/core/i18n/types';
import { useLocaleStore, useRecentSearchesStore, useUIStore } from '@/lib/core/stores';
import { cn } from '@/lib/core/utils/cn';
import type { AppContext } from '@/lib/core/types/modules';
import type {
  SearchSuggestionsDto,
  SectionContentResponseModel,
  ShopPopularSearchesElementContentModel,
} from '@/services/platform/api/model';
import { getCatalogSearchSuggestions } from '@/services/platform/data/shop/catalog';
import { ShopHeaderActions } from '@/components/shared/shop/layout/ShopHeaderActions';
import { ShopSearchResults, type ShopSearchResultFilter } from './results';

const SUGGESTION_DEBOUNCE_MS = 300;
/** A single character is the worst-case `LIKE '%x%'` over the whole catalogue and never yields a useful suggestion. */
const MIN_SUGGESTION_QUERY_LENGTH = 2;
const DEFAULT_SECTION_KEY = 'all';

interface SuggestionTerm {
  key: string;
  label: string;
  /** Set for product suggestions — selecting one navigates straight to the product detail page. */
  productGuid?: string;
  /** Set for category/subcategory/subSubcategory/collection suggestions — selecting one opens `ShopSearchResults`. */
  filter?: ShopSearchResultFilter;
}

interface SuggestionGroup {
  key: string;
  titleKey: TranslationKey;
  terms: SuggestionTerm[];
}

export interface ShopSearchProps {
  sections?: SectionContentResponseModel[];
  popular?: ShopPopularSearchesElementContentModel[];
  context?: AppContext;
}

/**
 * `ShopPopularSearchesPropertiesModel.searches` is typed `unknown` by Orval — the CMS
 * block-list shape isn't modeled. Handles it defensively: a plain string array, or an
 * `{ items: [...] }` block list whose items carry the term directly or under `.content`
 * (as a string or as `{ name | title | value }`).
 */
function extractSearchTerms(searches: unknown): string[] {
  const items = Array.isArray(searches)
    ? searches
    : Array.isArray((searches as { items?: unknown[] } | undefined)?.items)
      ? (searches as { items: unknown[] }).items
      : [];

  return items
    .map(item => {
      if (typeof item === 'string') return item;
      const content = (item as { content?: unknown })?.content ?? item;
      if (typeof content === 'string') return content;
      const named = content as { name?: string; title?: string; value?: string } | undefined;
      return named?.name ?? named?.title ?? named?.value ?? undefined;
    })
    .filter((term): term is string => Boolean(term));
}

/** Groups `SearchSuggestionsDto` by source (product/category/subcategory/…), each with its own translated title. */
function buildSuggestionGroups(data: SearchSuggestionsDto | undefined): SuggestionGroup[] {
  if (!data) return [];

  const groups: SuggestionGroup[] = [
    {
      key: 'products',
      titleKey: 'App.Shop.Search.Products',
      terms: (data.products ?? [])
        .filter(product => product.title && product.publicGuid)
        .map(product => ({
          key: `product-${product.publicGuid}`,
          label: product.title as string,
          productGuid: product.publicGuid,
        })),
    },
    {
      key: 'categories',
      titleKey: 'App.Shop.Search.Categories',
      terms: (data.categories ?? [])
        .filter(category => category.name)
        .map(category => {
          const slug = category.slug ?? (category.name as string);
          return {
            key: `category-${slug}`,
            label: category.name as string,
            filter: { type: 'category' as const, slug, label: category.name as string },
          };
        }),
    },
    {
      key: 'subcategories',
      titleKey: 'App.Shop.Search.Subcategories',
      terms: (data.subcategories ?? [])
        .filter(subcategory => subcategory.name)
        .map(subcategory => {
          const slug = subcategory.slug ?? (subcategory.name as string);
          return {
            key: `subcategory-${slug}`,
            label: subcategory.name as string,
            filter: { type: 'subcategory' as const, slug, label: subcategory.name as string },
          };
        }),
    },
    {
      key: 'subSubcategories',
      titleKey: 'App.Shop.Search.SubSubcategories',
      terms: (data.subSubcategories ?? [])
        .filter(subSubcategory => subSubcategory.name)
        .map(subSubcategory => {
          const slug = subSubcategory.slug ?? (subSubcategory.name as string);
          return {
            key: `subsubcategory-${slug}`,
            label: subSubcategory.name as string,
            filter: { type: 'subSubcategory' as const, slug, label: subSubcategory.name as string },
          };
        }),
    },
    {
      key: 'collections',
      titleKey: 'App.Shop.Search.Collections',
      terms: (data.collections ?? [])
        .filter(collection => collection.title && collection.umbracoNodeKey)
        .map(collection => ({
          key: `collection-${collection.umbracoNodeKey}`,
          label: collection.title as string,
          filter: {
            type: 'collection' as const,
            nodeKey: collection.umbracoNodeKey as string,
            label: collection.title as string,
          },
        })),
    },
  ];

  return groups.filter(group => group.terms.length > 0);
}

/** Bolds the portion of `label` matching `query` (case-insensitive), rest regular weight. */
function HighlightedTerm({ label, query }: { label: string; query: string }) {
  const matchIndex = label.toLowerCase().indexOf(query.toLowerCase());
  if (matchIndex === -1) return <>{label}</>;

  const before = label.slice(0, matchIndex);
  const match = label.slice(matchIndex, matchIndex + query.length);
  const after = label.slice(matchIndex + query.length);

  return (
    <>
      {before}
      <span className="font-semibold">{match}</span>
      {after}
    </>
  );
}

export function ShopSearch({ sections = [], popular = [], context }: ShopSearchProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const handleBack = () => navigate(-1);
  const { pathname } = useLocation();
  const locale = useLocaleStore(state => state.locale);
  const setScreenHeadOverride = useUIStore(state => state.setScreenHeadOverride);
  const termsBySection = useRecentSearchesStore(state => state.termsBySection);
  const addRecentSearch = useRecentSearchesStore(state => state.addTerm);
  const clearRecentSearches = useRecentSearchesStore(state => state.clear);
  const [searchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');

  const [activeSectionSlug, setActiveSectionSlug] = useState<string | undefined>(undefined);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestionsDto | undefined>(undefined);
  const [activeFilter, setActiveFilter] = useState<ShopSearchResultFilter | undefined>(undefined);

  useEffect(() => {
    if (activeSectionSlug !== undefined || sections.length === 0) return;
    const matched = sections.find(section => section.properties?.slug === sectionParam);
    setActiveSectionSlug((matched ?? sections[0]).properties?.slug);
  }, [sections, activeSectionSlug, sectionParam]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_SUGGESTION_QUERY_LENGTH) {
      setSuggestions(undefined);
      return;
    }

    // Aborting on cleanup cancels the superseded request on the server as well, not only its
    // result on the client — each keystroke past the debounce used to leave a full query running.
    // `signal.aborted` (not a request counter) gates the result, so a request that resolved just
    // before the abort still can't land after the text shrinks below the minimum.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      getCatalogSearchSuggestions({ q: trimmed, section: activeSectionSlug }, locale, controller.signal)
        .then(result => {
          if (!controller.signal.aborted) setSuggestions(result);
        })
        .catch(error => {
          if (controller.signal.aborted) return;
          console.error('ShopSearch: failed to fetch search suggestions', error);
          setSuggestions(undefined);
        });
    }, SUGGESTION_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [query, activeSectionSlug, locale]);

  const activeSection = sections.find(section => section.properties?.slug === activeSectionSlug);
  const sectionKey = activeSectionSlug ?? DEFAULT_SECTION_KEY;
  const recentSearches = termsBySection[sectionKey] ?? [];
  const popularItems = popular.filter(item => {
    const scopedSections = item.properties?.section;
    if (!scopedSections || scopedSections.length === 0) return true;
    return scopedSections.some(scoped => scoped.id === activeSection?.id);
  });
  const popularTerms = popularItems.flatMap(item => extractSearchTerms(item.properties?.searches));
  const suggestionGroups = buildSuggestionGroups(suggestions);

  const handleSelectTerm = (term: string) => {
    setQuery(term);
    addRecentSearch(sectionKey, term);
  };

  const handleSelectSuggestion = (term: SuggestionTerm) => {
    addRecentSearch(sectionKey, term.label);
    if (term.productGuid) {
      navigate(`${pathname}/product_${term.productGuid}`);
      return;
    }
    if (term.filter) {
      setActiveFilter(term.filter);
      return;
    }
    setQuery(term.label);
  };

  useEffect(() => {
    return () => setScreenHeadOverride(null);
  }, [setScreenHeadOverride]);

  useEffect(() => {
    if (activeFilter) return;
    setScreenHeadOverride({
      title: t('App.Shop.Search.Title'),
      titleSuffix: activeSection?.name,
      onBack: handleBack,
      ariaLabel: t('App.Labels.GoBack'),
      rightActions: <ShopHeaderActions context={context} hideSearch />,
    });
  }, [t, activeSection, handleBack, context, activeFilter, setScreenHeadOverride]);

  if (activeFilter) {
    return (
      <ShopSearchResults
        filter={activeFilter}
        sectionSlug={activeSectionSlug}
        sectionName={activeSection?.name}
        context={context}
        onBack={() => {
          setActiveFilter(undefined);
          setQuery('');
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6 bg-white px-4 pb-25">
      <div className="border-jncquoi-neutral-200 flex items-center gap-4 overflow-x-auto border-b">
        {sections.map(section => {
          const slug = section.properties?.slug;
          const isActive = slug === activeSectionSlug;
          return (
            <button
              key={section.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveSectionSlug(slug)}
              className="flex min-h-9 min-w-9 shrink-0 flex-col items-center justify-center gap-0.5"
            >
              <span
                className={cn(
                  'font-pp-neue-montreal text-jncquoi-neutral-1000 text-center text-[14px] leading-none tracking-[0.5px] whitespace-nowrap uppercase',
                  isActive ? 'font-semibold' : 'font-normal'
                )}
              >
                {section.name}
              </span>
              <span
                className={cn('h-px w-4 shrink-0', isActive ? 'bg-jncquoi-neutral-1000' : 'bg-transparent')}
                aria-hidden="true"
              />
            </button>
          );
        })}
      </div>

      <input
        type="text"
        value={query}
        onChange={event => setQuery(event.target.value)}
        placeholder={t('App.Shop.Search.Placeholder')}
        aria-label={t('App.Shop.Search.Placeholder')}
        autoFocus
        className="border-jncquoi-neutral-400 font-pp-neue-montreal text-jncquoi-neutral-1000 placeholder:text-jncquoi-neutral-700 w-full border-b py-2 text-[14px] font-semibold tracking-[0.5px] outline-none placeholder:uppercase"
      />

      {query.trim() ? (
        <div className="flex flex-col items-start gap-6">
          {suggestionGroups.map(group => (
            <div key={group.key} className="flex flex-col items-start">
              <p className="text-jncquoi-neutral-1000 font-pp-neue-montreal py-4 text-[12px] font-semibold tracking-[0.5px] uppercase">
                {t(group.titleKey)}
              </p>
              <div className="flex flex-col items-start gap-4 py-2">
                {group.terms.map(term => (
                  <button
                    key={term.key}
                    type="button"
                    onClick={() => handleSelectSuggestion(term)}
                    className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-left text-[14px] tracking-[0.5px]"
                  >
                    <HighlightedTerm label={term.label} query={query.trim()} />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {popularTerms.length > 0 && (
            <div className="flex flex-col items-start">
              <p className="text-jncquoi-neutral-1000 font-pp-neue-montreal py-4 text-[12px] tracking-[0.5px] uppercase">
                {t('App.Shop.Search.PopularSearches')}
              </p>
              <div className="w-full min-w-px scrollbar-none overflow-x-auto overflow-y-hidden py-2">
                <div className="flex shrink-0 items-center gap-6 pr-4">
                  {popularTerms.map(term => (
                    <button
                      key={term}
                      type="button"
                      onClick={() => handleSelectTerm(term)}
                      className="text-jncquoi-neutral-800 font-pp-neue-montreal shrink-0 text-[14px] tracking-[0.5px] whitespace-nowrap"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {recentSearches.length > 0 && (
            <div className="flex flex-col items-start">
              <div className="flex w-full items-center justify-between py-4">
                <p className="text-jncquoi-neutral-1000 font-pp-neue-montreal text-[12px] tracking-[0.5px] uppercase">
                  {t('App.Shop.Search.RecentSearches')}
                </p>
                <button
                  type="button"
                  onClick={() => clearRecentSearches(sectionKey)}
                  className="text-jncquoi-neutral-700 font-pp-neue-montreal text-[12px] tracking-[0.5px] uppercase"
                >
                  {t('App.Shop.Search.ClearAll')}
                </button>
              </div>
              <div className="w-full min-w-px scrollbar-none overflow-x-auto overflow-y-hidden py-2">
                <div className="flex shrink-0 items-center gap-6 pr-4">
                  {recentSearches.map(term => (
                    <button
                      key={term}
                      type="button"
                      onClick={() => handleSelectTerm(term)}
                      className="text-jncquoi-neutral-800 font-pp-neue-montreal shrink-0 text-[14px] tracking-[0.5px] whitespace-nowrap"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
